/**
 * VX-4754: post-bump recovery verification — the "no false recovery" bar for the production
 * self-heal.
 *
 * The ladder (VX-4284 pong gate / VX-4395 page-side idle gate → VX-4388 escalation → VX-4302
 * generation bump) already recovers a live session from a SharedWorker frame-delivery stall without a
 * manual reload. What it never did was check the remedy worked: `escapeHatchBump` fires the instant a
 * bump commits, so a bump that swapped to a fresh worker and STILL delivered nothing looked exactly
 * like one that resumed delivery in two seconds. Ops could only infer failure indirectly, from a
 * second bump arriving minutes later.
 *
 * This module is the pure judgment. A bump arms a pending verification; it is resolved by the first
 * delivered frame (recovered), by a superseding bump, or by the deadline below.
 */

import { IDLE_COLD_START_STALL_MS } from './idleDeliveryWatchdog';

/**
 * How long a freshly bumped generation has to deliver its first frame before the bump is judged to
 * have failed. Derived from the cold-start gate rather than a fresh literal, because that is exactly
 * the situation a post-bump connection is in — a brand-new worker that has never delivered a frame —
 * and the two must not drift apart: a deadline shorter than the gate would call a genuinely slow
 * first frame (large series, signed-URL latency, decode backlog) a failed self-heal.
 *
 * A bump that actually works is far faster than this (the OPFS cache survives the swap, so the first
 * frame typically lands within seconds); the deadline only bounds the wait before declaring failure
 * when nothing else resolves the verification.
 */
export const BUMP_RECOVERY_DEADLINE_MS = IDLE_COLD_START_STALL_MS;

/**
 * `recovered` — a frame arrived at the bumped generation, so delivery is provably restored.
 * `unrecovered` — no frame arrived while this tab still had demand the worker never answered: the
 * self-heal was attempted and did not work, which is the case that must never report healthy.
 * `no-demand` — no frame arrived, but nothing was waiting on one either (an idle or fully loaded tab,
 * or a manual bump on a loaded study). Not evidence either way, so it is reported as its own outcome
 * rather than folded into a failure.
 */
export type BumpRecoveryOutcome = 'recovered' | 'unrecovered' | 'no-demand';

export interface PendingBumpVerification {
  /** The generation the bump moved this tab TO — the one being judged. */
  generation: number;
  /** Epoch ms the bump was observed locally (committed here, or adopted from a peer). */
  bumpedAt: number;
}

export interface BumpRecoveryJudgment {
  outcome: BumpRecoveryOutcome;
  /** Bump → first frame for `recovered`; bump → judgment time for the other two. */
  elapsedMs: number;
}

/**
 * Judge a pending verification. `frameAt` is the epoch ms of the first frame delivered at the bumped
 * generation, or null when the verification is being resolved without one (deadline elapsed, or a
 * superseding bump proved the ladder re-exhausted). `hasUnmetDemand` is whether this tab still has
 * demand the worker has not answered at the bumped generation — see `unmetDemandStore`.
 */
export function judgeBumpRecovery({
  pending,
  frameAt,
  hasUnmetDemand,
  now,
}: {
  pending: PendingBumpVerification;
  frameAt: number | null;
  hasUnmetDemand: boolean;
  now: number;
}): BumpRecoveryJudgment {
  // Clamped because both clocks are wall-clock reads: a backwards clock correction between the bump
  // and the frame must not report a negative recovery latency.
  if (frameAt != null) {
    return { outcome: 'recovered', elapsedMs: Math.max(0, frameAt - pending.bumpedAt) };
  }
  const elapsedMs = Math.max(0, now - pending.bumpedAt);
  return { outcome: hasUnmetDemand ? 'unrecovered' : 'no-demand', elapsedMs };
}
